import {useEffect, useState} from 'react';
import type {List} from 'immutable';
import sessionStore from 'client/Session/SessionStore';

import {
  getAllSchoolsUserIsAdminQuery,
  getClassroomQuery,
  getSchoolQuery,
  getTeacherWithMetadataQuery
} from './hooks.utils';

interface hasProUpgradeResponse {
  // `undefined` while the pro-license lookup is in flight; guard against it
  // when gating on "definitely non-pro" to avoid racing with the loading window.
  hasProUpgrade: boolean | undefined;
  fetchingProUpgrade: boolean;
  error: Error | undefined;
}

/**
 * helper function that gets the role of the user at a school
 *
 * @param {string} schoolId id of the school
 * @returns {any} the role of the user at the school
 */
const getRoleOfUserAtSchool = async (schoolId: string) => {
  try {
    // Dynamic JSON:API mandark resource; narrow to the getters used here.
    const schoolWithPersonnelInformation =
      (await getTeacherWithMetadataQuery().getResourcePromise()) as {
        getSchoolPersonnel: () => Array<{
          getSchool: () => {getId: () => string};
          getStatus: () => string;
        }>;
      };
    const schoolPersonnelArray = schoolWithPersonnelInformation.getSchoolPersonnel();
    const schoolRole = schoolPersonnelArray.find(
      (personnel) => personnel.getSchool().getId() === schoolId
    );
    if (!schoolRole) {
      return null;
    }
    return schoolRole;
  } catch (e) {
    throw new Error(`useHasProUpgrade: Error getting user's role at school ${e}`);
  }
};

/**
 * helper function to get all the schools where the user is an admin
 *
 * @param {string} adminId the id of the admin
 * @returns {any} the schools where the user is an admin
 */
export const getSchoolsOfAdmin = async (adminId: string): Promise<List<unknown>> => {
  try {
    // Dynamic JSON:API mandark resource; the schools collection is an Immutable List.
    const schoolsWhereUserIsAdmin = (await getAllSchoolsUserIsAdminQuery(
      adminId
    ).getResourcePromise()) as List<unknown>;
    return schoolsWhereUserIsAdmin;
  } catch (e) {
    throw new Error(`useHasProUpgrade: Error getting schools of admin ${e}`);
  }
};

/**
 * helper function to check if a school has a pro upgrade
 *
 * @param {any} school mandark school_v5 resource with school_licenses_v1.upgrades_v1 included
 * @returns {boolean} true or false depending on if one of the liscenses is active and has a pro upgrade. If any errors in the process, it returns false
 */
export const schoolHasProUpgrade = (school) => {
  let hasProUpgrade = false;
  try {
    const liscenses = school.getSchoolLicenses();
    liscenses.forEach((license) => {
      if (license.active) {
        license.upgrades.forEach((upgrade) => {
          if (upgrade.upgrade_type === 'pro') {
            hasProUpgrade = true;
          }
        });
      }
    });
    return hasProUpgrade;
  } catch {
    return false;
  }
};

/**
 * This hook checks if a school has a pro upgrade or if a classroom is associated with a school that has a pro upgrade
 * @param {string} type the type of the resource to check for pro upgrade. This should be school or classroom
 * @param {string} id the id of the resource to check for pro upgrade
 * @returns {hasProUpgradeResponse} hasProUpgradeResponse and fetchingProUpgrade
 */
export function useHasProUpgrade(
  type: string, // 'classrooms' | 'schools'
  id: string
): hasProUpgradeResponse {
  const [hasProUpgrade, setHasProUpgrade] = useState<boolean | undefined>(undefined);
  const [fetchingProUpgrade, setFetchingProUpgrade] = useState(true);
  const [error, setError] = useState<Error>();

  useEffect(() => {
    let subscribed = true;
    const fetchActiveSubscriptions = async () => {
      if (!subscribed) return;
      try {
        setFetchingProUpgrade(true);
        setHasProUpgrade(undefined);
        if (sessionStore.isSuper()) {
          setHasProUpgrade(true);
          return;
        }
        let schoolId;
        if (type === 'classrooms') {
          // Dynamic JSON:API mandark resource; narrow to the getters used here.
          const classroom = (await getClassroomQuery(id).getResourcePromise()) as {
            getSchool: () => {getId: () => string};
          };
          schoolId = classroom.getSchool().getId();
          if (!subscribed) return;
        } else if (type === 'schools') {
          schoolId = id;
        } else if (type === 'school-admin') {
          const adminId = id === 'me' ? sessionStore.getUserId() : id;
          const schoolsWhereUserIsAdmin = await getSchoolsOfAdmin(adminId);
          const schoolsThatDontHaveProUpgrade = schoolsWhereUserIsAdmin.filter(
            (school) => !schoolHasProUpgrade(school)
          );
          if (subscribed) {
            setHasProUpgrade(schoolsThatDontHaveProUpgrade.size === 0);
          }
          return;
        } else {
          // if the type is not school, classroom, or school-admin, we don't have to check for pro upgrade
          setHasProUpgrade(false);
          return;
        }
        const userRole = await getRoleOfUserAtSchool(schoolId);

        if (!userRole || userRole.getStatus() !== 'approved') {
          // if user is not approved at a certain school, they do not have access to any of features under pro upgrades
          if (subscribed) {
            setHasProUpgrade(false);
          }
          return;
        }
        if (!subscribed) return;

        const school = await getSchoolQuery(schoolId).getResourcePromise();

        if (subscribed) {
          setHasProUpgrade(schoolHasProUpgrade(school));
        }
      } catch (e) {
        if (subscribed) {
          setError(e as Error);
          setHasProUpgrade(false);
        }
      } finally {
        if (subscribed) {
          setFetchingProUpgrade(false);
        }
      }
    };
    if (type && id) fetchActiveSubscriptions();

    return () => {
      subscribed = false;
    };
  }, [type, id]);

  return {
    hasProUpgrade,
    fetchingProUpgrade,
    error
  };
}
